import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findFreeSpot, type LayoutBody, type LayoutDoc } from "../../shared/layout";
import { presetByKey, PRESETS } from "../../shared/presets";
import { ApiRequestError } from "../data/api";
import { readPref, writePref } from "../data/persist";
import { useLayouts, useMe, useSettings } from "../data/queries";
import { Wordmark } from "../ui/Brand";
import { Appearance, useOnline } from "./Appearance";
import { useDevicePrefs } from "./devicePrefs";
import { PhoneNav } from "./PhoneNav";
import { layoutForMode, useScreenDirector } from "./screenDirector";
import { useHousehold } from "./useHousehold";
import { Dashboard } from "../layout/Dashboard";
import { useLayoutMutations } from "../layout/layoutApi";
import { useLayoutDraft } from "../layout/useLayoutDraft";
import { AddWidgetSheet, WidgetSettingsSheet } from "../layout/WidgetSheets";
import { SettingsPanel } from "../settings/SettingsPanel";
import { Icon, type IconName } from "../ui/Icon";
import { ErrorBoundary } from "../ui/ErrorBoundary";
import { Confirm, Sheet } from "../ui/Sheet";
import type { WidgetDefinition } from "../widgets/types";
import { AdminApp } from "../admin/AdminApp";
import "../widgets/alerts/alerts.css";
import { AlertBar } from "./AlertBar";
import { HeadsUp } from "./HeadsUp";
import { KeepWarm } from "../data/keepWarm";
import { useAlertDirector } from "./alertDirector";
import { SignIn } from "./SignIn";
import { ApproveLink } from "./LinkSignIn";
import { JoinInvite } from "./Passkeys";
import { DemoBadge } from "./Demo";
import { useScreens } from "./useScreens";
import { Screensaver } from "../widgets/photos/Screensaver";
import { SleepScreen, useSleep } from "./Sleep";
import { usePixelShift } from "./pixelShift";

// Today: the Almanac's ranked view of the day (sky, weather windows, holidays, the family's day).
const TodayScreen = lazy(() => import("../today/TodayScreen").then((m) => ({ default: m.TodayScreen })));
const TODAY_ID = "today";
// The Living Atlas is its own screen (not a widget layout); its map code loads only when opened.
const LivingAtlasScreen = lazy(() => import("../atlas/LivingAtlasScreen").then((m) => ({ default: m.LivingAtlasScreen })));
const ATLAS_ID = "atlas";
// The whiteboard is a full screen too (one board per day).
const WhiteboardScreen = lazy(() => import("../whiteboard/WhiteboardScreen").then((m) => ({ default: m.WhiteboardScreen })));
const WHITEBOARD_ID = "whiteboard";
// Drive: where we're headed, the drive from home and Google's directions map.
const DriveScreen = lazy(() => import("../drive/DriveScreen").then((m) => ({ default: m.DriveScreen })));
const DRIVE_ID = "drive";
// Lookout: webcams near home, at the mountains and around the world.
const LookoutScreen = lazy(() => import("../lookout/LookoutScreen").then((m) => ({ default: m.LookoutScreen })));
const LOOKOUT_ID = "lookout";
// Resorts: one of the household's ski areas at a time, everything about the day there.
const ResortsScreen = lazy(() => import("../resorts/ResortsScreen").then((m) => ({ default: m.ResortsScreen })));
const RESORTS_ID = "resorts";
// Screens that show the heads-up strip (watches, advisories, powder days, roads to the mountains).
const HEADS_UP_SCREENS = new Set(["family-home", "morning"]);
import { AppUiContext, type AppUi, type SettingsSection } from "./uiContext";

interface Toast {
  id: number;
  message: string;
  action?: { label: string; run: () => void };
}

// Own screens in the phone's "More" sheet, in order.
const OWN_NAV: { id: string; name: string; icon: IconName }[] = [
  { id: TODAY_ID, name: "Today", icon: "sparkle" },
  { id: ATLAS_ID, name: "Living Atlas", icon: "globe" },
  { id: RESORTS_ID, name: "Resorts", icon: "ski" },
  { id: DRIVE_ID, name: "Drive", icon: "car" },
  { id: LOOKOUT_ID, name: "Lookout", icon: "camera" },
  { id: WHITEBOARD_ID, name: "Whiteboard", icon: "board" },
];

const LAYOUT_ICONS: Record<string, IconName> = {
  morning: "sun",
  "family-home": "home",
  "full-calendar": "calendar",
  "weather-focus": "cloud",
  chores: "star",
  kitchen: "utensils",
  photos: "image",
  night: "moon",
  alerts: "alert",
  outdoors: "mountain",
  news: "news",
};

export function App() {
  // A family member's passkey invite works before (and without) signing in.
  if (window.location.pathname === "/join") return <JoinInvite />;
  return <SignedInApp />;
}

function SignedInApp() {
  const me = useMe();
  if (me.error instanceof ApiRequestError && me.error.status === 401) return <SignIn />;
  if (!me.data) {
    return (
      <div className="splash">
        <Wordmark />
        {me.isError ? (
          <>
            <p>Couldn’t reach Kinmantle.</p>
            <button className="btn" onClick={() => me.refetch()}>
              Try again
            </button>
          </>
        ) : (
          <p className="muted">Loading…</p>
        )}
      </div>
    );
  }
  // A phone approving a screen's sign-in (QR code on that screen's sign-in page).
  if (window.location.pathname === "/link") return <ApproveLink me={me.data} />;
  // The admin page shares sign-in and data with the display; it's just another route.
  if (window.location.pathname.startsWith("/admin")) {
    return (
      <>
        <Appearance />
        <AdminApp />
        {me.data.demo && <DemoBadge />}
      </>
    );
  }
  return (
    <>
      <Home />
      {me.data.demo && <DemoBadge />}
    </>
  );
}

function Home() {
  const layouts = useLayouts();
  const settings = useSettings();
  const mutations = useLayoutMutations();
  const draft = useLayoutDraft();

  const [activeId, setActiveId] = useState<string | null>(() => readPref<string | null>("activeLayout", null));
  const [editing, setEditing] = useState(false);
  const [configuring, setConfiguring] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [layoutMenu, setLayoutMenu] = useState(false);
  const [settingsSection, setSettingsSection] = useState<SettingsSection | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastSeq = useRef(0);

  const { timeZone, settings: hs } = useHousehold();
  const device = useDevicePrefs();
  // Screens turned off in Admin leave the navigation, the schedule and background work.
  const screens = useScreens();
  const atlasEnabled = screens.isOn(ATLAS_ID);
  const open = (id: string) => activeId === id && screens.isOn(id);
  const atlasOpen = open(ATLAS_ID);
  const boardOpen = open(WHITEBOARD_ID);
  const driveOpen = open(DRIVE_ID);
  const lookoutOpen = open(LOOKOUT_ID);
  const todayOpen = open(TODAY_ID);
  const resortsOpen = open(RESORTS_ID);
  const ownScreen = atlasOpen || boardOpen || driveOpen || lookoutOpen || todayOpen || resortsOpen;
  const ownId = ownScreen ? (activeId ?? undefined) : undefined;
  const [atlasAmbient, setAtlasAmbient] = useState(false);
  const [lookoutTour, setLookoutTour] = useState(false);
  const list = useMemo(() => {
    const all = [...(layouts.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const shown = all.filter((l) => screens.isOn(l.id));
    // Admin keeps one layout on; an older saved copy can't leave the wall with none.
    return shown.length ? shown : all;
  }, [layouts.data, screens]);

  const selectLayout = useCallback((id: string) => {
    setActiveId(id);
    // Remembered per device, so the wall and a phone can show different layouts.
    writePref("activeLayout", id);
  }, []);
  const alertDirector = useAlertDirector({
    enabled: device.autoScreens && hs.alertTakeover,
    layouts: list,
    activeId: activeId ?? undefined,
    editing,
    select: selectLayout,
  });
  const director = useScreenDirector({
    enabled: device.autoScreens,
    schedule: hs.schedule,
    idleMinutes: hs.idleReturnMinutes,
    timeZone,
    layouts: list,
    activeId: activeId ?? undefined,
    editing,
    // Ambient rotation on the Living Atlas is deliberate; don't pull the wall away from it.
    // So is a Lookout tour.
    hold: alertDirector.takeoverPending || (atlasOpen && atlasAmbient) || (lookoutOpen && lookoutTour),
    select: selectLayout,
  });
  const sleep = useSleep({ enabled: device.autoScreens, hold: editing || alertDirector.takeoverPending, bedtime: hs.bedtime, timeZone });
  usePixelShift(device.autoScreens && device.pixelShift);
  const active: LayoutDoc | undefined =
    list.find((l) => l.id === activeId) ??
    (device.autoScreens ? layoutForMode(director.mode, hs.schedule, list) : undefined) ??
    list.find((l) => l.id === settings.data?.defaultLayoutId) ??
    list.find((l) => l.presetKey === "family-home") ??
    list[0];

  const toast = useCallback((message: string, action?: Toast["action"]) => {
    const id = ++toastSeq.current;
    setToasts((t) => [...t.slice(-2), { id, message, action }]);
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), action ? 7000 : 4000);
  }, []);

  const activeLayoutId = active?.id;
  const ui: AppUi = useMemo(
    () => ({
      openSettings: (s) => setSettingsSection(s ?? "household"),
      toast,
      openScreen: (presetKey) => {
        const target = list.find((l) => l.presetKey === presetKey);
        if (!target || target.id === activeLayoutId || editing) return false;
        selectLayout(target.id);
        return true;
      },
    }),
    [toast, list, activeLayoutId, editing, selectLayout],
  );

  const startEditing = () => {
    if (!active || ownScreen) return;
    draft.begin({ grid: active.grid, items: active.items });
    setSaveError(null);
    setEditing(true);
  };

  const stopEditing = () => {
    draft.end();
    setEditing(false);
    setConfiguring(null);
  };

  const finishEditing = () => {
    if (!active || !draft.draft) return;
    if (!draft.dirty) return stopEditing();
    mutations.save.mutate(
      { id: active.id, revision: active.revision, name: active.name, body: draft.draft },
      {
        onSuccess: () => {
          stopEditing();
          toast("Layout saved");
        },
        onError: (e) => {
          setSaveError(e instanceof ApiRequestError && e.code === "REVISION_CONFLICT" ? `${e.message}` : `Couldn’t save: ${e.message}`);
        },
      },
    );
  };

  const addWidget = (def: WidgetDefinition) => {
    setAdding(false);
    draft.apply((b) => {
      const spot = findFreeSpot(b.items, def.defaultSize.w, def.defaultSize.h, b.grid) ?? findFreeSpot(b.items, def.minSize.w, def.minSize.h, b.grid);
      if (!spot) {
        toast("No room on this layout — shrink or remove a widget first.");
        return b;
      }
      const id = `w_${crypto.randomUUID().slice(0, 8)}`;
      return { ...b, items: [...b.items, { id, type: def.type, version: def.version, config: {}, wall: spot }] };
    });
  };

  const body: LayoutBody | null = editing ? draft.draft : active ? { grid: active.grid, items: active.items } : null;
  const configItem = configuring && draft.draft ? draft.draft.items.find((i) => i.id === configuring) : undefined;
  const preset = active ? presetByKey(active.presetKey) : undefined;

  return (
    <AppUiContext.Provider value={ui}>
      <Appearance />
      <KeepWarm offKeys={screens.offQueryKeys} />
      {device.autoScreens && director.mode === "night" && !editing && <div className="night-dim" aria-hidden="true" />}
      {/* Bedtime belongs to the sleep screen, not bright photos. */}
      <Screensaver hold={editing || alertDirector.takeoverPending || sleep.bedtime} night={device.autoScreens && director.mode === "night"} />
      {sleep.asleep && <SleepScreen onWake={sleep.wake} />}
      <div className={`app ${editing ? "app-editing" : ""}`}>
        <NavRail
          layouts={list}
          activeId={ownId ?? active?.id}
          today={screens.isOn(TODAY_ID) ? () => selectLayout(TODAY_ID) : undefined}
          atlas={atlasEnabled ? () => selectLayout(ATLAS_ID) : undefined}
          resorts={screens.isOn(RESORTS_ID) ? () => selectLayout(RESORTS_ID) : undefined}
          drive={screens.isOn(DRIVE_ID) ? () => selectLayout(DRIVE_ID) : undefined}
          lookout={screens.isOn(LOOKOUT_ID) ? () => selectLayout(LOOKOUT_ID) : undefined}
          whiteboard={screens.isOn(WHITEBOARD_ID) ? () => selectLayout(WHITEBOARD_ID) : undefined}
          editing={editing}
          onSelect={selectLayout}
          onSettings={() => setSettingsSection("household")}
        />
        <PhoneNav
          layouts={list}
          activeId={ownId ?? active?.id}
          own={OWN_NAV.filter((s) => screens.isOn(s.id)).map((s) => ({ ...s, onOpen: () => selectLayout(s.id) }))}
          ownOpen={ownScreen}
          editing={editing}
          pinned={director.pinned}
          showPin={device.autoScreens}
          icons={LAYOUT_ICONS}
          onPin={() => director.setPinned(!director.pinned)}
          onSelect={selectLayout}
          onEdit={startEditing}
          onSettings={() => setSettingsSection("household")}
        />
        <div className="main-col">
          {editing && (
            <div className="edit-bar" role="toolbar" aria-label="Layout editing">
              <span className="edit-pill">Editing</span>
              <button className="btn btn-ghost" onClick={() => setLayoutMenu(true)}>
                {active?.name} <Icon name="chevronDown" size="1rem" />
              </button>
              <span className="spacer" />
              <button className="btn" onClick={() => setAdding(true)}>
                <Icon name="plus" size="1.1rem" /> Add widget
              </button>
              <button className="icon-btn" onClick={draft.undo} disabled={!draft.canUndo} aria-label="Undo">
                <Icon name="undo" />
              </button>
              <button className="icon-btn" onClick={draft.redo} disabled={!draft.canRedo} aria-label="Redo">
                <Icon name="redo" />
              </button>
              <button className="btn btn-ghost" onClick={() => (draft.dirty ? setConfirmCancel(true) : stopEditing())}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={finishEditing} disabled={mutations.save.isPending}>
                <Icon name="check" size="1.1rem" /> {mutations.save.isPending ? "Saving…" : "Done editing"}
              </button>
            </div>
          )}
        {saveError && editing && (
          <div className="banner banner-error" role="alert">
            {saveError}
            <button
              className="btn btn-sm"
              onClick={() => {
                stopEditing();
                void mutations.refresh();
              }}
            >
              Discard my changes & reload
            </button>
          </div>
        )}

        {!editing && alertDirector.prominent.length > 0 && active?.id !== alertDirector.target?.id && (
          <AlertBar alerts={alertDirector.prominent} onOpen={() => alertDirector.target && selectLayout(alertDirector.target.id)} />
        )}
        {!editing && !ownScreen && HEADS_UP_SCREENS.has(active?.presetKey ?? "") && <HeadsUp />}
        <main className="main">
          {/* A screen that crashes keeps the navigation; switching screens tries again. */}
          <ErrorBoundary key={ownId ?? active?.id ?? "none"} title="This screen hit a problem">
          {atlasOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <LivingAtlasScreen onAmbientChange={setAtlasAmbient} />
            </Suspense>
          ) : boardOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <WhiteboardScreen />
            </Suspense>
          ) : driveOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <DriveScreen />
            </Suspense>
          ) : todayOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <TodayScreen />
            </Suspense>
          ) : lookoutOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <LookoutScreen onTourChange={setLookoutTour} />
            </Suspense>
          ) : resortsOpen && !editing ? (
            <Suspense fallback={<div className="widget-loading" />}>
              <ResortsScreen />
            </Suspense>
          ) : body ? (
            <Dashboard
              body={body}
              editing={editing}
              onChange={(items) => draft.apply((b) => ({ ...b, items }))}
              onConfigure={setConfiguring}
              onRemove={(id) => draft.apply((b) => ({ ...b, items: b.items.filter((i) => i.id !== id) }))}
            />
          ) : layouts.isError ? (
            <div className="splash">
              <p>Couldn’t load your layouts.</p>
              <button className="btn" onClick={() => layouts.refetch()}>
                Try again
              </button>
            </div>
          ) : (
            <div className="widget-loading" />
          )}
          </ErrorBoundary>
          {!editing && !ownScreen && body && (
            <ScreenTools pinned={director.pinned} showPin={device.autoScreens} onPin={() => director.setPinned(!director.pinned)} onEdit={startEditing} />
          )}
        </main>
        </div>

        {adding && <AddWidgetSheet onPick={addWidget} onClose={() => setAdding(false)} />}
        {configItem && (
          <WidgetSettingsSheet
            item={configItem}
            onChange={(config) => draft.apply((b) => ({ ...b, items: b.items.map((i) => (i.id === configItem.id ? { ...i, config } : i)) }))}
            onClose={() => setConfiguring(null)}
          />
        )}
        {layoutMenu && active && (
          <LayoutMenu
            layout={active}
            hasPreset={Boolean(preset)}
            onClose={() => setLayoutMenu(false)}
            onRestore={() => {
              if (preset) draft.apply(() => structuredClone(preset.body));
              setLayoutMenu(false);
            }}
            onCreated={(doc) => {
              stopEditing();
              selectLayout(doc.id);
              setLayoutMenu(false);
              toast(`Created “${doc.name}”`);
            }}
            onDeleted={() => {
              stopEditing();
              setLayoutMenu(false);
              const next = list.find((l) => l.id !== active.id);
              if (next) selectLayout(next.id);
            }}
            dirty={draft.dirty}
          />
        )}
        {confirmCancel && (
          <Confirm
            title="Discard changes?"
            message="Your layout changes haven’t been saved."
            confirmLabel="Discard"
            danger
            onCancel={() => setConfirmCancel(false)}
            onConfirm={() => {
              setConfirmCancel(false);
              stopEditing();
            }}
          />
        )}
        {settingsSection && <SettingsPanel initial={settingsSection} onClose={() => setSettingsSection(null)} layouts={list} />}

        <div className="toasts" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className="toast">
              <span>{t.message}</span>
              {t.action && (
                <button
                  className="link-btn"
                  onClick={() => {
                    t.action!.run();
                    setToasts((all) => all.filter((x) => x.id !== t.id));
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </AppUiContext.Provider>
  );
}

const TOOLS_AWAKE_MS = 5000;

/**
 * Pin and Edit layout: small icons in the screen's corner instead of rows in
 * the rail (phones have them in PhoneNav). They show while someone is touching
 * or pointing at the screen and fade when it's left alone, so they never sit
 * over a widget's corner; a pinned lock stays faintly visible.
 */
function ScreenTools({ pinned, showPin, onPin, onEdit }: { pinned: boolean; showPin: boolean; onPin: () => void; onEdit: () => void }) {
  const [awake, setAwake] = useState(false);
  useEffect(() => {
    let timer = 0;
    const wake = () => {
      setAwake(true);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setAwake(false), TOOLS_AWAKE_MS);
    };
    document.addEventListener("pointerdown", wake, true);
    document.addEventListener("pointermove", wake, { passive: true });
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("pointerdown", wake, true);
      document.removeEventListener("pointermove", wake);
    };
  }, []);
  return (
    <div className={`screen-tools ${awake ? "" : "asleep"}`}>
      {showPin && (
        <button
          className={`screen-tool ${pinned ? "on" : ""}`}
          onClick={onPin}
          aria-pressed={pinned}
          aria-label={pinned ? "Unpin this screen" : "Pin this screen"}
          title={pinned ? "Pinned: this screen stays until tomorrow morning" : "Keep this screen up"}
        >
          <Icon name={pinned ? "lock" : "unlock"} size="1.05rem" />
        </button>
      )}
      <button className="screen-tool" onClick={onEdit} aria-label="Edit layout" title="Edit layout">
        <Icon name="layout" size="1.05rem" />
      </button>
    </div>
  );
}

function NavRail({
  layouts,
  activeId,
  today,
  atlas,
  resorts,
  drive,
  lookout,
  whiteboard,
  editing,
  onSelect,
  onSettings,
}: {
  layouts: LayoutDoc[];
  activeId: string | undefined;
  // Each own screen's opener; absent while it's turned off.
  today?: () => void;
  atlas?: () => void;
  resorts?: () => void;
  drive?: () => void;
  lookout?: () => void;
  whiteboard?: () => void;
  editing: boolean;
  onSelect: (id: string) => void;
  onSettings: () => void;
}) {
  const online = useOnline();
  return (
    <nav className="rail" aria-label="Kinmantle">
      <div className="rail-brand">
        <Wordmark />
      </div>
      <div className="rail-items">
        {today && (
          <button className={`rail-item ${activeId === TODAY_ID ? "on" : ""}`} onClick={today} disabled={editing} aria-current={activeId === TODAY_ID ? "page" : undefined}>
            <Icon name="sparkle" size="1.45rem" />
            <span>Today</span>
          </button>
        )}
        {layouts.map((l) => (
          <button
            key={l.id}
            className={`rail-item ${l.id === activeId ? "on" : ""}`}
            onClick={() => onSelect(l.id)}
            disabled={editing && l.id !== activeId}
            aria-current={l.id === activeId ? "page" : undefined}
          >
            <Icon name={LAYOUT_ICONS[l.presetKey ?? ""] ?? "layout"} size="1.45rem" />
            <span>{l.name}</span>
          </button>
        ))}
        {atlas && (
          <button className={`rail-item ${activeId === ATLAS_ID ? "on" : ""}`} onClick={atlas} disabled={editing} aria-current={activeId === ATLAS_ID ? "page" : undefined}>
            <Icon name="globe" size="1.45rem" />
            <span>Living Atlas</span>
          </button>
        )}
        {resorts && (
          <button className={`rail-item ${activeId === RESORTS_ID ? "on" : ""}`} onClick={resorts} disabled={editing} aria-current={activeId === RESORTS_ID ? "page" : undefined}>
            <Icon name="ski" size="1.45rem" />
            <span>Resorts</span>
          </button>
        )}
        {drive && (
          <button className={`rail-item ${activeId === DRIVE_ID ? "on" : ""}`} onClick={drive} disabled={editing} aria-current={activeId === DRIVE_ID ? "page" : undefined}>
            <Icon name="car" size="1.45rem" />
            <span>Drive</span>
          </button>
        )}
        {lookout && (
          <button className={`rail-item ${activeId === LOOKOUT_ID ? "on" : ""}`} onClick={lookout} disabled={editing} aria-current={activeId === LOOKOUT_ID ? "page" : undefined}>
            <Icon name="camera" size="1.45rem" />
            <span>Lookout</span>
          </button>
        )}
        {whiteboard && (
          <button className={`rail-item ${activeId === WHITEBOARD_ID ? "on" : ""}`} onClick={whiteboard} disabled={editing} aria-current={activeId === WHITEBOARD_ID ? "page" : undefined}>
            <Icon name="board" size="1.45rem" />
            <span>Whiteboard</span>
          </button>
        )}
      </div>
      <div className="rail-foot">
        {!online && (
          <span className="rail-status" role="status">
            <Icon name="wifiOff" size="1.1rem" />
            <span>Offline</span>
          </span>
        )}
        <button className="rail-item" onClick={onSettings}>
          <Icon name="gear" size="1.45rem" />
          <span>Settings</span>
        </button>
      </div>
    </nav>
  );
}

function LayoutMenu({
  layout,
  hasPreset,
  dirty,
  onClose,
  onRestore,
  onCreated,
  onDeleted,
}: {
  layout: LayoutDoc;
  hasPreset: boolean;
  dirty: boolean;
  onClose: () => void;
  onRestore: () => void;
  onCreated: (doc: LayoutDoc) => void;
  onDeleted: () => void;
}) {
  const m = useLayoutMutations();
  const [name, setName] = useState(layout.name);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const rename = () =>
    m.save.mutate(
      { id: layout.id, revision: layout.revision, name, body: { grid: layout.grid, items: layout.items } },
      { onSuccess: onClose, onError: (e) => setError(e.message) },
    );

  return (
    <Sheet title="Layout" onClose={onClose} size="sm">
      <div className="form">
        <label className="field">
          <span>Name</span>
          <div className="row">
            <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} />
            <button className="btn" onClick={rename} disabled={dirty || !name.trim() || name === layout.name} title={dirty ? "Finish or cancel your edits first" : undefined}>
              Rename
            </button>
          </div>
        </label>
        {hasPreset && (
          <button className="btn btn-block" onClick={onRestore}>
            <Icon name="undo" size={18} /> Restore this layout’s defaults
          </button>
        )}
        <button className="btn btn-block" onClick={() => m.create.mutate({ name: `${layout.name} copy`, copyFrom: layout.id }, { onSuccess: onCreated, onError: (e) => setError(e.message) })}>
          <Icon name="copy" size={18} /> Save a copy (of the last saved version)
        </button>
        <div className="field">
          <span>New layout from a starter</span>
          <div className="chip-row wrap">
            {PRESETS.map((p) => (
              <button key={p.key} className="chip" onClick={() => m.create.mutate({ name: p.name, presetKey: p.key }, { onSuccess: onCreated, onError: (e) => setError(e.message) })}>
                {p.name}
              </button>
            ))}
            <button className="chip" onClick={() => m.create.mutate({ name: "Blank layout" }, { onSuccess: onCreated, onError: (e) => setError(e.message) })}>
              Blank
            </button>
          </div>
        </div>
        <button className="btn btn-danger-ghost btn-block" onClick={() => setConfirmDelete(true)}>
          <Icon name="trash" size={18} /> Delete this layout
        </button>
        {error && <p className="error-text">{error}</p>}
      </div>
      {confirmDelete && (
        <Confirm
          title={`Delete “${layout.name}”?`}
          message="This removes the layout for every screen in the household."
          confirmLabel="Delete layout"
          danger
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => m.remove.mutate(layout.id, { onSuccess: onDeleted, onError: (e) => setError(e.message) })}
        />
      )}
    </Sheet>
  );
}
